import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Box,
  Typography,
  FormControl,
  TextField,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DescriptionIcon from "@mui/icons-material/Description";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import GavelIcon from "@mui/icons-material/Gavel";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { X, BookOpen, Search, Home, User, Calendar, Hash } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { fetchOptions } from "../../actions/searchActions";
import CustomYearRangePicker from "../layout/CustomYearRangePicker";

const journals = [
  "CLC",
  "CLCN",
  "CLD",
  "CLR",
  "FSC",
  "GBLR",
  "KLR",
  "LHC",
  "MLD",
  "NLR",
  "PCRLJ",
  "PCRLJN",
  "PCTLR",
  "PCrLJ",
  "PLC",
  "PLC(CS)",
  "PLC(CS)N",
  "PLCN",
  "PLD",
  "PLJ",
  "PSC",
  "PSC CRI",
  "PTCL",
  "PTD",
  "SBLR",
  "SCMR",
  "SCP",
  "SCR",
  "SD",
  "TAX",
  "YLP",
  "YLR",
  "YLRN",
];

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear; year >= 1947; year--) {
    years.push(year.toString());
  }
  return years;
};

const generatePageOptions = () => {
  const pages = [];
  for (let page = 1; page <= 2755; page++) {
    pages.push(page.toString());
  }
  return pages;
};

const yearOptions = generateYearOptions();
const pageOptions = generatePageOptions();

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const Facet = ({
  facetsData,
  handleChange,
  selectedValues,
  showChips,
  handleClearChip,
  filterMode,
  startYear,
  setStartYear,
  endYear,
  setEndYear,
}) => {
  const [options, setOptions] = useState([]);
  const [lawyerOptions, setLawyerOptions] = useState([]);
  const [judgeOptions, setJudgeOptions] = useState([]);
  const [journalOptions, setJournalOptions] = useState(journals);
  const [yearError, setYearError] = useState("");
  const [pageError, setPageError] = useState("");
  const [loading, setLoading] = React.useState(false);
  const [loadingJudge, setLoadingJudge] = React.useState(false);
  const [loadingCourt, setLoadingCourt] = React.useState(false);
  const [loadingJournal, setLoadingJournal] = React.useState(false);
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState("");
  const [inputJudge, setInputJudge] = useState("");
  const [inputCourt, setInputCourt] = useState("");
  const [inputJournal, setInputJournal] = useState("");
  const searchResultsState = useSelector((state) => state.searchResults);
  const { dataFetched } = searchResultsState;

  const handleDeleteChip = (category) => {
    handleClearChip(category);
  };

  const handleSelect = (category, value) => {
    handleChange(category, value);
    setInputValue("");
    setInputJudge("");
    setLoading(false);
    setLoadingCourt(false);
    setLoadingJudge(false);
    setLoadingJournal(false);
  };

  // Debounced API call
  const debouncedFetchOptions = useCallback(
    debounce((value, collection, selectedValues) => {
      dispatch(fetchOptions(value, collection, selectedValues));
    }, 500),
    [], // Delay in milliseconds
  );

  const filterOptions = useCallback(
    (input, category) => {
      const optionsForCategory = facetsData?.find(
        (facet) => facet.category === category,
      )?.options;

      const filteredStartsWith = optionsForCategory?.filter((option) => {
        const optionText =
          typeof option === "string" ? option : option.name || option.formatted;
        return optionText?.toLowerCase().startsWith(input.toLowerCase());
      });
      const filteredIncludes = optionsForCategory?.filter((option) => {
        const optionText =
          typeof option === "string" ? option : option.name || option.formatted;
        return optionText?.toLowerCase().includes(input.toLowerCase());
      });

      return [
        ...new Set([
          ...(filteredStartsWith || []),
          ...(filteredIncludes || []),
        ]),
      ].slice(0, 5);
    },
    [facetsData],
  );

  const handleInputChange = useCallback(
    (event, value) => {
      setInputCourt(value);
      setLoadingCourt(true);
      if (value && value.length > 2) {
        debouncedFetchOptions(value, "Court", selectedValues);
      } else {
        setOptions([]);
        setLoadingCourt(false);
      }
    },
    [dispatch, facetsData, dataFetched],
  );

  const handleLawyerInputChange = useCallback(
    (event, value) => {
      setInputValue(value);
      setLoading(true);
      if (value && value.length > 2) {
        debouncedFetchOptions(value, "Lawyer_Name", selectedValues);
      } else {
        setLawyerOptions([]);
        setLoading(false);
      }
    },
    [dispatch, facetsData, dataFetched],
  );

  const handleJudgeInputChange = useCallback(
    (event, value) => {
      setInputJudge(value);
      setLoadingJudge(true);
      if (value && value.length > 2) {
        debouncedFetchOptions(value, "Judge_Name", selectedValues);
      } else {
        setJudgeOptions([]);
        setLoadingJudge(false);
      }
    },
    [dispatch, facetsData, dataFetched],
  );

  const handleJournalInputChange = useCallback(
    (event, value) => {
      setInputJournal(value);
      //setLoadingJournal(true);
      // if (value && value.length > 1) {
      //   debouncedFetchOptions(value, "Section", selectedValues);
      // } else {
      //   setJournalOptions([]);
      //   setLoadingJournal(false);
      // }
    },
    [dispatch, facetsData, dataFetched],
  );

  const handleYearInput = (event, category) => {
    const value = event.target.value;
    // Only allow numeric input (non-decimal digits)
    if (/^\d*$/.test(value)) {
      debouncedFetchOptions(value, "Year", selectedValues);
      handleSelect(category, value);
      if (value.length === 4 || value === "") {
        // If exactly 4 digits, reset the error and update selected values
        setYearError("");
        //handleSelect(category, value);
      } else if (value.length < 4 || value.length > 4) {
        // If less than 4 digits, show a warning but allow typing
        setYearError("Year must be exactly 4 digits.");
      }
    } else {
      // For non-numeric input, show an error
      setYearError("Please enter a valid 4-digit year");
    }

    // Optional: Clear the chip if value is empty
    if (!value && showChips[category] && filterMode === "standard") {
      handleDeleteChip(category);
    }
  };

  const handlePageInput = (event, category) => {
    const value = event.target.value;
    // Only allow non-decimal numeric input
    // Only allow numeric input (non-decimal digits)
    if (/^\d*$/.test(value)) {
      handleSelect(category, value);
      if ((value.length > 0 && value.length < 5) || value === "") {
        // If exactly 4 digits, reset the error and update selected values
        setPageError("");
        //handleSelect(category, value);
      } else if (value.length > 4) {
        // If less than 4 digits, show a warning but allow typing
        setPageError("Page number must be exactly 4 digits.");
      }
    } else {
      // For non-numeric input, show an error
      setPageError("Please enter a valid 4-digit page number");
    }
    if (!value && showChips[category] && filterMode === "standard") {
      handleDeleteChip(category);
    }
  };

  useEffect(() => {
    if (dataFetched) {
      const filteredLawyerOptions = filterOptions(inputValue, "Lawyer");
      setLawyerOptions(filteredLawyerOptions);
      setLoading(false);
      const filteredJudgeOptions = filterOptions(inputJudge, "Judge");
      setJudgeOptions(filteredJudgeOptions);
      setLoadingJudge(false);
      const filteredCourtOptions = filterOptions(inputCourt, "Court");
      setOptions(filteredCourtOptions);
      setLoadingCourt(false);
      const filteredJournalOptions = filterOptions(inputJournal, "Journal");
      setJournalOptions(filteredJournalOptions);
      setLoadingJournal(false);
    }
  }, [facetsData, dataFetched]);

  useEffect(() => {
    setYearError("");
    setPageError("");
  }, [filterMode]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {filterMode === "standard" ? (
        <Box>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#1F2937",
              mb: 0.5,
              ml: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <BookOpen size={16} color="#0073A6" />
            Journal
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
            <Autocomplete
              size="small"
              value={selectedValues?.["Journal"] || null}
              options={journals || []}
              onChange={(event, newValue) => {
                handleSelect("Journal", newValue);
              }}
              onInputChange={(e, newValue) => {
                handleJournalInputChange(e, newValue);
                if (
                  !newValue &&
                  showChips?.["Journal"] &&
                  filterMode === "standard"
                ) {
                  // handleDeleteChip("Journal");
                }
              }}
              loading={loadingJournal}
              forcePopupIcon={true}
              popupIcon={
                <ArrowDropDownIcon
                  sx={{ fontSize: "1rem", color: "#003D78" }}
                />
              }
              disableClearable={!selectedValues?.["Journal"]}
              clearIcon={selectedValues?.["Journal"] ? <X size={16} /> : null}
              isOptionEqualToValue={(option, value) =>
                option === value || (value === "" && option === null)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Journal"
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(0, 61, 120, 0.2)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 2px rgba(0, 61, 120, 0.1)",
                        borderColor: "#003D78",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.75rem",
                      padding: "8px 12px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "0.75rem",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingJournal ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "8px",
                    "& .MuiAutocomplete-listbox": {
                      fontSize: "0.75rem",
                      maxHeight: "200px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": {
                        width: "4px",
                      },
                      "::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "::-webkit-scrollbar-thumb": {
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "4px",
                      },
                      "& .MuiAutocomplete-option": {
                        padding: "8px 12px",
                        "&[aria-selected='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.08)",
                        },
                        "&[data-focus='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.04)",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </FormControl>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#1F2937",
              mb: 0.5,
              ml: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Home size={16} color="#F59E0B" />
            Court
          </Typography>

          <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
            <Autocomplete
              size="small"
              value={selectedValues?.["Court"] || ""}
              isOptionEqualToValue={(option, value) =>
                option.formatted === value?.formatted
              }
              getOptionLabel={(option) => option.formatted || ""}
              options={options || []}
              onChange={(event, newValue) => {
                handleSelect("Court", newValue);
              }}
              onInputChange={(e, newValue) => {
                handleInputChange(e, newValue);
                if (!newValue && showChips?.["Court"]) {
                  handleDeleteChip("Court");
                }
              }}
              loading={loadingCourt}
              forcePopupIcon={true}
              popupIcon={
                <ArrowDropDownIcon
                  sx={{ fontSize: "1rem", color: "#003D78" }}
                />
              }
              disableClearable={!selectedValues?.["Court"]}
              clearIcon={selectedValues?.["Court"] ? <X size={16} /> : null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Enter Court Name"
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(0, 61, 120, 0.2)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 2px rgba(0, 61, 120, 0.1)",
                        borderColor: "#003D78",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.75rem",
                      padding: "8px 12px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "0.75rem",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCourt ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "8px",
                    "& .MuiAutocomplete-listbox": {
                      fontSize: "0.75rem",
                      maxHeight: "200px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": {
                        width: "4px",
                      },
                      "::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "::-webkit-scrollbar-thumb": {
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "4px",
                      },
                      "& .MuiAutocomplete-option": {
                        padding: "8px 12px",
                        "&[aria-selected='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.08)",
                        },
                        "&[data-focus='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.04)",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </FormControl>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#1F2937",
              mb: 0.5,
              ml: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <User size={16} color="#10B981" />
            Judge
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
            <Autocomplete
              size="small"
              isOptionEqualToValue={(option, value) =>
                option.formatted === value?.formatted
              }
              getOptionLabel={(option) => option.formatted || ""}
              // open={openLawyerOptions && options.length > 0}
              value={selectedValues?.["Judge"] || ""}
              options={judgeOptions || []}
              onChange={(event, newValue) => {
                handleSelect("Judge", newValue);
              }}
              onInputChange={(e, newValue) => {
                handleJudgeInputChange(e, newValue);
                if (!newValue && showChips?.["Judge"]) {
                  handleDeleteChip("Judge");
                }
              }}
              loading={loadingJudge}
              forcePopupIcon={true}
              popupIcon={
                <ArrowDropDownIcon
                  sx={{ fontSize: "1rem", color: "#003D78" }}
                />
              }
              clearIcon={selectedValues?.["Judge"] ? <X size={16} /> : null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Enter Judge Name"
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(0, 61, 120, 0.2)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 2px rgba(0, 61, 120, 0.1)",
                        borderColor: "#003D78",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.75rem",
                      padding: "8px 12px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "0.75rem",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingJudge ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "8px",
                    "& .MuiAutocomplete-listbox": {
                      fontSize: "0.75rem",
                      maxHeight: "200px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": {
                        width: "4px",
                      },
                      "::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "::-webkit-scrollbar-thumb": {
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "4px",
                      },
                      "& .MuiAutocomplete-option": {
                        padding: "8px 12px",
                        "&[aria-selected='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.08)",
                        },
                        "&[data-focus='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.04)",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </FormControl>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#1F2937",
              mb: 0.5,
              ml: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <User size={16} color="#EF4444" />
            Lawyer
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
            <Autocomplete
              size="small"
              // open={openLawyerOptions && options.length > 0}
              value={selectedValues?.["Lawyer"] || ""}
              isOptionEqualToValue={(option, value) =>
                option.formatted === value?.formatted
              }
              getOptionLabel={(option) => option.formatted || ""}
              options={lawyerOptions || []}
              onChange={(event, newValue) => {
                handleSelect("Lawyer", newValue);
              }}
              onInputChange={(e, newValue) => {
                handleLawyerInputChange(e, newValue);
                if (!newValue && showChips?.["Lawyer"]) {
                  handleDeleteChip("Lawyer");
                }
              }}
              loading={loading}
              forcePopupIcon={true}
              popupIcon={
                <ArrowDropDownIcon
                  sx={{ fontSize: "1rem", color: "#003D78" }}
                />
              }
              clearIcon={selectedValues?.["Lawyer"] ? <X size={16} /> : null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Enter Lawyer Name"
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(0, 61, 120, 0.2)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 2px rgba(0, 61, 120, 0.1)",
                        borderColor: "#003D78",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.75rem",
                      padding: "8px 12px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "0.75rem",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "8px",
                    "& .MuiAutocomplete-listbox": {
                      fontSize: "0.75rem",
                      maxHeight: "200px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": {
                        width: "4px",
                      },
                      "::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "::-webkit-scrollbar-thumb": {
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "4px",
                      },
                      "& .MuiAutocomplete-option": {
                        padding: "8px 12px",
                        "&[aria-selected='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.08)",
                        },
                        "&[data-focus='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.04)",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </FormControl>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#1F2937",
              mb: 0.5,
              ml: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Calendar size={16} color="#8B5CF6" />
            Year
          </Typography>
          <Box
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "rgba(0, 61, 120, 0.2)",
                },
                "&.Mui-focused": {
                  boxShadow: "0 0 0 2px rgba(0, 61, 120, 0.1)",
                  borderColor: "#003D78",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "0.75rem",
                padding: "8px 12px",
              },
              "& .MuiInputBase-input::placeholder": {
                fontSize: "0.75rem",
              },
            }}
          >
            <CustomYearRangePicker
              startYear={startYear}
              endYear={endYear}
              onStartYearChange={setStartYear}
              onEndYearChange={setEndYear}
              Journal={selectedValues?.["Journal"]}
              handleSelect={handleSelect}
              selectedValues={selectedValues}
              setYearError={setYearError}
            />
          </Box>
        </Box>
      ) : (
        <Box sx={{ mb: 1 }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#1F2937",
              mb: 0.5,
              ml: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <BookOpen size={16} color="#0073A6" />
            Journal
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
            <Autocomplete
              size="small"
              value={selectedValues?.["Journal"] || null}
              options={journals || []}
              onChange={(event, newValue) => handleSelect("Journal", newValue)}
              onInputChange={(e, newValue) => {
                handleJournalInputChange(e, newValue);
                if (
                  !newValue &&
                  showChips?.["Journal"] &&
                  filterMode === "standard"
                ) {
                  handleDeleteChip("Journal");
                }
              }}
              loading={loadingJournal}
              forcePopupIcon={true}
              popupIcon={
                <ArrowDropDownIcon
                  sx={{ fontSize: "1rem", color: "#003D78" }}
                />
              }
              disableClearable={!selectedValues?.["Journal"]}
              clearIcon={selectedValues?.["Journal"] ? <X size={16} /> : null}
              isOptionEqualToValue={(option, value) =>
                option === value || (value === "" && option === null)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Enter Journal Name"
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(0, 61, 120, 0.2)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 2px rgba(0, 61, 120, 0.1)",
                        borderColor: "#003D78",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.75rem",
                      padding: "8px 12px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "0.75rem",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingJournal ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "8px",
                    "& .MuiAutocomplete-listbox": {
                      fontSize: "0.75rem",
                      maxHeight: "200px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": {
                        width: "4px",
                      },
                      "::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "::-webkit-scrollbar-thumb": {
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "4px",
                      },
                      "& .MuiAutocomplete-option": {
                        padding: "8px 12px",
                        "&[aria-selected='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.08)",
                        },
                        "&[data-focus='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.04)",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </FormControl>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#1F2937",
              mb: 0.5,
              ml: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Calendar size={16} color="#8B5CF6" />
            Year
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
            <Autocomplete
              size="small"
              value={selectedValues?.["Year"] || null}
              options={yearOptions || []}
              onChange={(event, newValue) => {
                handleSelect("Year", newValue);
              }}
              onInputChange={(e, newValue) => {
                handleYearInput({ target: { value: newValue } }, "Year");
              }}
              forcePopupIcon={true}
              popupIcon={
                <ArrowDropDownIcon
                  sx={{ fontSize: "1rem", color: "#003D78" }}
                />
              }
              disableClearable={!selectedValues?.["Year"]}
              clearIcon={
                selectedValues?.["Year"] ? <ClearIcon fontSize="small" /> : null
              }
              isOptionEqualToValue={(option, value) =>
                option === value || (value === "" && option === null)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Enter Year (e.g. 2023)"
                  variant="outlined"
                  error={!!yearError}
                  helperText={yearError ? yearError : ""}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(0, 61, 120, 0.2)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 2px rgba(0, 61, 120, 0.1)",
                        borderColor: "#003D78",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.75rem",
                      padding: "8px 12px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "8px",
                    "& .MuiAutocomplete-listbox": {
                      fontSize: "0.75rem",
                      maxHeight: "200px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "::-webkit-scrollbar-track": {
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "3px",
                      },
                      "::-webkit-scrollbar-thumb": {
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "4px",
                      },
                      "& .MuiAutocomplete-option": {
                        padding: "8px 12px",
                        "&[aria-selected='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.08)",
                        },
                        "&[data-focus='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.04)",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </FormControl>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#1F2937",
              mb: 0.5,
              ml: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Hash size={16} color="#7C3AED" />
            Page
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
            <Autocomplete
              size="small"
              value={selectedValues?.["Page"] || null}
              options={pageOptions || []}
              onChange={(event, newValue) => {
                handleSelect("Page", newValue);
              }}
              onInputChange={(e, newValue) => {
                handlePageInput({ target: { value: newValue } }, "Page");
              }}
              forcePopupIcon={true}
              popupIcon={
                <ArrowDropDownIcon
                  sx={{ fontSize: "1rem", color: "#003D78" }}
                />
              }
              disableClearable={!selectedValues?.["Page"]}
              clearIcon={
                selectedValues?.["Page"] ? <ClearIcon fontSize="small" /> : null
              }
              isOptionEqualToValue={(option, value) =>
                option === value || (value === "" && option === null)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Enter Page Number (e.g. 0123)"
                  variant="outlined"
                  error={!!pageError}
                  helperText={pageError ? pageError : ""}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(0, 61, 120, 0.2)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 2px rgba(0, 61, 120, 0.1)",
                        borderColor: "#003D78",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.75rem",
                      padding: "8px 12px",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              )}
              componentsProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    borderRadius: "8px",
                    "& .MuiAutocomplete-listbox": {
                      fontSize: "0.75rem",
                      maxHeight: "200px",
                      overflowY: "auto",
                      "::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "::-webkit-scrollbar-track": {
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "3px",
                      },
                      "::-webkit-scrollbar-thumb": {
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "4px",
                      },
                      "& .MuiAutocomplete-option": {
                        padding: "8px 12px",
                        "&[aria-selected='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.08)",
                        },
                        "&[data-focus='true']": {
                          backgroundColor: "rgba(0, 61, 120, 0.04)",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </FormControl>
        </Box>
      )}
    </Box>
  );
};

export default Facet;
